'use client';

import { useEffect, useId, useState } from 'react';

import type { TDynamicData } from '../../types/dynamic-data';
import type { Member } from '../../types/member';

import { Tabs, TabsContent, TabsList, TabsTrigger } from '@eliadmin/core/src/components/ui/tabs';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { HtmlDescription } from '../ui/html-description';
import { ImageComp } from '../shared/image-comp';
import { FileDownload } from './file-download';
import { AboutMembers } from './about-members';
import { fetchDynamicSections } from '@eliadmin/core/src/api/dynamics/dynamics-api';
import { useQuery } from '@tanstack/react-query';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';

const addTargetBlankToLinks = (htmlContent: string): string => {
  if (!htmlContent) return htmlContent;
  return htmlContent.replace(/<a(?![^>]*target=)([^>]*)>/gi, '<a$1 target="_blank">');
};

interface AboutUsTabsProps {
  tabs: TDynamicData[];
  members: Member[];
  statutes: TDynamicData[];
  codes: TDynamicData[];
  locale: string;
}

export function AboutUsTabs({ tabs, members, statutes, codes, locale }: AboutUsTabsProps) {
  const { t } = useTranslations();
  const [activeTab, setActiveTab] = useState<string>(tabs[0]?.name || '');
  const tabsId = useId();
  const panelId = `${tabsId}-panel`;
  const activeTriggerIndex = tabs.findIndex(tab => tab.name === activeTab);

  const goals = locale === 'sk' ? 'Ciele' : 'Goals';
  const goalsHash = locale === 'sk' ? '#ciele' : '#goals';

  useEffect(() => {
    const hash = window.location.hash;
    if (hash === goalsHash) {
      setActiveTab(goals);
    } else {
      setActiveTab(tabs[0]?.name || '');
    }
  }, [tabs]);

  const activeTabData = tabs.find(tab => tab.name === activeTab);

  const { data: documents = [] } = useQuery({
    queryKey: ['TAB_DOCUMENTS', locale, activeTab],
    queryFn: () =>
      fetchDynamicSections<TDynamicData>({
        locale,
        sectionIds: [12],
        query: { valueFilters: [{ fieldId: 27, values: activeTab }] },
      }),
  });

  const modifiedContent = activeTabData?.fields[1]?.value
    ? addTargetBlankToLinks(activeTabData.fields[1].value)
    : '';

  return (
    <section className={cn('custom-section', 'mb-10')}>
      <Tabs
        value={activeTab}
        onValueChange={setActiveTab}
        className={cn(
          'custom-container',
          'flex flex-col items-start justify-start gap-10 sm:gap-12',
        )}
      >
        <TabsList
          className={cn(
            'flex h-fit w-full items-end justify-start gap-0 overflow-x-scroll rounded-none border-none bg-transparent p-[1px] shadow-none sm:gap-2 sm:overflow-x-hidden',
            'hide-scroll-bar',
          )}
        >
          {tabs.map((tab, index) => (
            <TabsTrigger
              key={tab.name}
              value={tab.name}
              id={`${tabsId}-trigger-${index}`}
              aria-controls={panelId}
              className={cn(
                'ea-about-us__tabs-trigger',
                'h-fit rounded-none border-b-[3px] p-4 text-base font-bold sm:text-lg lg:text-xl',
              )}
            >
              {tab.name}
            </TabsTrigger>
          ))}
          <div className="h-[3px] w-full flex-grow bg-primary/50" />
        </TabsList>
        <TabsContent
          value={activeTab}
          id={panelId}
          aria-labelledby={activeTriggerIndex >= 0 ? `${tabsId}-trigger-${activeTriggerIndex}` : undefined}
          className="flex w-full flex-col-reverse items-start justify-start gap-10 md:flex-row lg:gap-12 xl:gap-20"
        >
          <ImageComp
            alt={activeTab}
            imageUrl={activeTabData?.image?.url ?? ''}
            className={{ container: 'max-w-[357px]' }}
            priority={true}
            loading="eager"
          />
          <div className="flex w-full flex-col items-start justify-start gap-10">
            <h3 className="text-2xl font-bold text-primary sm:text-3xl">
              {activeTabData?.name ?? ''}
            </h3>
            {modifiedContent && (
              <HtmlDescription
                content={modifiedContent}
                className={cn(
                  'w-full text-justify md:max-w-[558px]',
                  'ea-about-us__tabs-description',
                )}
              />
            )}
            {activeTab === t('text2407') && (
              <div className="flex w-full flex-col items-start justify-start gap-2.5">
                {statutes.map(item => (
                  <FileDownload item={item} key={item.name} />
                ))}
              </div>
            )}
            {activeTab === t('text2408') && (
              <div className="flex w-full flex-col items-start justify-start gap-2.5">
                {codes.map(item => (
                  <FileDownload item={item} key={item.name} />
                ))}
              </div>
            )}
            {activeTab === t('text2409') && <AboutMembers members={members} />}
            {documents.length > 0 && (
              <div className="flex w-full flex-col items-start justify-start gap-4">
                <h4 className="text-lg font-semibold sm:text-xl">{t('text2406')}</h4>
                <div className="flex w-full flex-col items-start justify-start gap-2">
                  {documents.map((doc: TDynamicData) => (
                    <FileDownload item={doc} key={doc.name} />
                  ))}
                </div>
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>
    </section>
  );
}
